// packages/ui/src/TickerCarousel.tsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';

interface TickerCarouselProps {
  items: string[];
  intervalSeconds?: number;
  itemHeightRem: number;
  activeColor?: string;
  inactiveColor?: string;
  className?: string; // FIX: Added className prop to allow custom styling (replaces direct 'style')
}

export const TickerCarousel: React.FC<TickerCarouselProps> = ({
  items,
  intervalSeconds = 2.5,
  itemHeightRem,
  activeColor = 'text-emerald-400 [.theme-liquid-glass_&]:text-violet-300',
  inactiveColor = 'text-slate-500 [.theme-liquid-glass_&]:text-white/60',
  className, // FIX: New prop
}) => {
  const [index, setIndex] = useState(0);
  const hasItems = Array.isArray(items) && items.length > 0;

  useEffect(() => {
    // Also guard the effect in case the prop changes to an invalid value later
    if (!hasItems) return;

    const timer = setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1) % items.length);
    }, intervalSeconds * 1000);

    return () => clearInterval(timer);
  }, [hasItems, items, intervalSeconds]);

  if (!hasItems) {
    return null;
  }

  const containerHeightStyle = { height: `${itemHeightRem}rem` };

  return (
    <div
      style={containerHeightStyle}
      className={clsx("inline-flex overflow-hidden relative", className)} // FIX: Apply className here
    >
      <div
        className="absolute inset-0 z-10"
        style={{
          maskImage: 'linear-gradient(to bottom, transparent, black 25%, black 75%, transparent)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent, black 25%, black 75%, transparent)',
        }}
      />
      <motion.div
        className="relative"
        animate={{ y: `-${index * itemHeightRem}rem` }}
        transition={{ type: 'spring', stiffness: 200, damping: 25 }}
      >
        {items.map((item, i) => {
          const isActive = i === index;
          return (
            <motion.div
              key={i}
              className="flex items-center justify-start whitespace-nowrap" // CRITICAL: justify-start
              style={containerHeightStyle}
              animate={{
                scale: isActive ? 1 : 0.7,
                opacity: isActive ? 1 : 0.4,
              }}
              transition={{ type: 'spring', stiffness: 200, damping: 25 }}
            >
              <span className={clsx('transition-colors', isActive ? activeColor : inactiveColor)}>
                {item}
              </span>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
};
